import { useEffect, useRef, useState } from 'react';

import { native, useRafThrottle } from '@muzly/shared';
import type { PitchTrackerEvent } from '@muzly/shared';
import { clampNum } from '@muzly/voice-engine';

/**
 * InputLevelMeter — 컨셉 "입력 음량" 라이브 버전.
 *
 * pitchTrackerEvent의 rmsDb(pitch_frame / input_level)를 받아 무음~큰소리를
 * 가로 바로 시각화한다. dB는 음수(무음≈-90dB, 큰소리≈0dB)이므로 표시 범위로
 * 클램프해 0~1 비율로 매핑한다. 표시 바닥을 -90dB로 잡아 silence_threshold_db
 * (config -90~-20)를 게이트선으로 얹을 수 있게 한다.
 *
 * 47fps 입력을 매 프레임 setState 하지 않는다(#9 패턴): 최신 dB는 ref에 모으고
 * 렌더는 rAF로 ~30fps throttle.
 *
 * 불변식: JS로는 Analysis Frame(rmsDb)만 건너온다 — raw PCM 없음.
 */

const MIN_DB = -90; // 무음 바닥 (silence_threshold_db 하한과 정렬)
const MAX_DB = 0; // 큰소리 천장

/** dB(음수)를 바 채움 비율(0~1)로 — 표시 범위 밖은 클램프. */
export const dbToFraction = (
  db: number,
  minDb = MIN_DB,
  maxDb = MAX_DB,
): number => {
  if (!Number.isFinite(db)) {
    return 0;
  }
  return clampNum((db - minDb) / (maxDb - minDb), 0, 1);
};

const rmsDbOf = (event: PitchTrackerEvent): number | null => {
  if (event.type === 'pitch_frame') {
    return event.frame.rmsDb;
  }
  if (event.type === 'input_level') {
    return event.rmsDb;
  }
  return null;
};

interface InputLevelMeterProps {
  /** 현재 config의 silence_threshold_db. 주면 바 위에 게이트선을 얹는다. */
  silenceThresholdDb?: number | null;
}

export const InputLevelMeter = ({
  silenceThresholdDb = null,
}: InputLevelMeterProps = {}) => {
  const dbRef = useRef<number | null>(null);
  const [db, setDb] = useState<number | null>(null);

  useEffect(() => {
    return native.on('pitchTrackerEvent', (event) => {
      if (
        event.type === 'session_started' ||
        event.type === 'session_stopped'
      ) {
        dbRef.current = null;
        return;
      }
      const rms = rmsDbOf(event);
      if (rms != null) {
        dbRef.current = rms;
      }
    });
  }, []);

  // rAF로 ~30fps throttle 렌더: 매 이벤트가 아니라 tick마다 최신 dB를 state로 올린다.
  useRafThrottle(() => setDb(dbRef.current));

  const fraction = db == null ? 0 : dbToFraction(db);
  const hasGate =
    silenceThresholdDb != null && Number.isFinite(silenceThresholdDb);
  const gateFraction = hasGate ? dbToFraction(silenceThresholdDb as number) : 0;
  // 게이트 위(threshold 이상)면 voiced 후보 — 톤으로 표시.
  const aboveGate =
    hasGate && db != null && db >= (silenceThresholdDb as number);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">입력 음량</span>
        <span className="font-mono text-xs text-muted-foreground">
          {db == null ? '—' : `${db.toFixed(1)} dB`}
        </span>
      </div>
      <div
        className="relative h-3 w-full overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label="입력 음량"
        aria-valuemin={MIN_DB}
        aria-valuemax={MAX_DB}
        aria-valuenow={db ?? MIN_DB}
      >
        <div
          className="h-full rounded-full transition-[width] duration-75 ease-out"
          style={{
            width: `${(fraction * 100).toFixed(1)}%`,
            background: aboveGate
              ? 'hsl(var(--primary))'
              : 'hsl(var(--muted-foreground))',
          }}
        />
        {hasGate && (
          <span
            className="pointer-events-none absolute inset-y-0 w-px bg-amber-500"
            style={{ left: `${(gateFraction * 100).toFixed(1)}%` }}
            aria-hidden
          />
        )}
      </div>
      {hasGate && (
        <span className="font-mono text-[11px] text-muted-foreground">
          silence gate {(silenceThresholdDb as number).toFixed(0)} dBFS
        </span>
      )}
    </section>
  );
};
